import React, { useState } from "react";
import { Check, Plus, Shirt, Trash2, X } from "lucide-react";
import { BoutonSombre, Case, Eyebrow } from "./ui";

/* Forfaits Compétition / Compagnie : nom, prix du semestre, costume compris ou non, et les cours
   rattachés. C'est le planning qui porte l'appartenance des cours : cocher un cours ici
   écrit FORFAIT et PRIX_FORFAIT sur ce cours (journalisé comme toute modification). */
export function Forfaits({ forfaits, cours, onCreer, onModifier, onSupprimer }) {
  const [edite, setEdite] = useState(null);
  const [nouveau, setNouveau] = useState(false);

  return (
    <div className="space-y-3" data-testid="forfaits-editeur">
      <div className="grid md:grid-cols-3 gap-3">
        {forfaits.map((f) => (
          <div key={f.nom} className="bg-white rounded-2xl p-5 border"
            style={{ borderColor: f.prix_divergents?.length ? "#FECACA" : "var(--border)" }}
            data-testid={`forfait-${f.nom.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-sm font-semibold">{f.nom}</div>
            <div className="font-display text-2xl mt-1">{f.prix.toLocaleString("fr-CH")} CHF</div>
            <div className="text-[11px] mt-2 flex items-center gap-1.5"
              style={{ color: f.costume_inclus ? "#15803D" : "#B45309" }}>
              <Shirt size={12} /> costume {f.costume_inclus ? "compris dans le forfait" : "facturé en plus"}
            </div>
            <div className="text-[11px] text-[var(--muted)] mt-1">
              {f.entrainements_actifs ?? f.entrainements.length} entraînement(s) par semaine, indissociables :
              le prix est facturé une seule fois par élève, quel que soit le nombre d'entraînements suivis.
            </div>
            <ul className="text-[11px] text-[var(--muted)] mt-2 space-y-0.5">
              {f.entrainements.map((e) => (
                <li key={e.id}>· {e.jour} {e.debut} — {e.nom}
                  {!(e.statut || "").toLowerCase().startsWith("actif") && " (inactif)"}</li>
              ))}
            </ul>
            {f.entrainements_incomplets && (
              <div className="text-[11px] mt-2" style={{ color: "#B45309" }}>
                {f.entrainements_actifs} entraînement(s) au planning pour {f.entrainements_attendus} attendu(s) :
                vérifie la colonne FORFAIT du planning, une famille pourrait s'inscrire à un demi-forfait au prix plein.
              </div>
            )}
            {f.prix_divergents?.length > 0 && (
              <div className="text-[11px] mt-2" style={{ color: "#B91C1C" }}>
                Deux prix différents dans le planning : {f.prix_divergents.join(" / ")} CHF.
              </div>
            )}
            <div className="flex gap-3 mt-3">
              <button onClick={() => setEdite(f)} className="text-xs font-semibold" style={{ color: "var(--primary)" }}
                data-testid={`forfait-modifier-${f.nom.toLowerCase().replace(/\s+/g, "-")}`}>Modifier</button>
              <button onClick={() => onSupprimer(f.nom)} className="text-xs flex items-center gap-1" style={{ color: "#B91C1C" }}
                data-testid={`forfait-supprimer-${f.nom.toLowerCase().replace(/\s+/g, "-")}`}>
                <Trash2 size={12} /> Supprimer
              </button>
            </div>
          </div>
        ))}
        <button
          onClick={() => setNouveau(true)}
          className="rounded-2xl p-5 border border-dashed flex flex-col items-center justify-center gap-2 text-sm"
          style={{ borderColor: "var(--primary)", color: "var(--primary)", minHeight: 160 }}
          data-testid="forfait-nouveau"
        >
          <Plus size={20} /> Nouveau forfait
        </button>
      </div>

      {(edite || nouveau) && (
        <Editeur
          forfait={edite}
          cours={cours}
          onFermer={() => { setEdite(null); setNouveau(false); }}
          onValider={async (data) => {
            const ok = edite ? await onModifier(edite.nom, data) : await onCreer(data);
            if (ok) { setEdite(null); setNouveau(false); }
          }}
        />
      )}
    </div>
  );
}

function Editeur({ forfait, cours, onFermer, onValider }) {
  const [nom, setNom] = useState(forfait?.nom || "");
  const [prix, setPrix] = useState(forfait?.prix || "");
  const [costume, setCostume] = useState(!!forfait?.costume_inclus);
  const [choisis, setChoisis] = useState((forfait?.entrainements || []).map((e) => e.id));
  const [q, setQ] = useState("");

  const liste = cours
    .filter((c) => !c.forfait || choisis.includes(c.id) || c.forfait === forfait?.nom)
    .filter((c) => !q || `${c.nom} ${c.discipline} ${c.jour}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--primary)" }} data-testid="forfait-editeur">
      <div className="flex items-center gap-3">
        <Eyebrow>{forfait ? `Modifier « ${forfait.nom} »` : "Nouveau forfait"}</Eyebrow>
        <button onClick={onFermer} className="ml-auto text-[var(--muted)]" data-testid="forfait-editeur-fermer"><X size={16} /></button>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mt-4">
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Nom du forfait</span>
          <input className="input-field mt-1" value={nom} onChange={(e) => setNom(e.target.value)} data-testid="forfait-nom" />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Prix du semestre CHF</span>
          <input className="input-field mt-1" type="number" value={prix} onChange={(e) => setPrix(e.target.value)} data-testid="forfait-prix" />
        </label>
        <div>
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Costume</span>
          <span
            className="flex items-center gap-2 mt-2 text-sm cursor-pointer select-none"
            onClick={() => setCostume(!costume)}
            data-testid="forfait-costume"
          >
            <Case cochee={costume} onChange={setCostume} testid="forfait-costume-case" />
            Compris dans le forfait
          </span>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center gap-3">
          <Eyebrow>Cours rattachés ({choisis.length})</Eyebrow>
          <input
            className="input-field ml-auto"
            style={{ width: 220, paddingTop: 7, paddingBottom: 7 }}
            placeholder="Filtrer les cours…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="forfait-filtre-cours"
          />
        </div>
        <div className="mt-2 max-h-56 overflow-auto rounded-xl border" style={{ borderColor: "var(--border-soft)" }}>
          {liste.map((c) => (
            <button
              key={c.id}
              onClick={() => setChoisis(choisis.includes(c.id) ? choisis.filter((x) => x !== c.id) : [...choisis, c.id])}
              className="w-full flex items-center gap-3 px-3 py-2 text-left text-xs border-b hover:bg-[var(--primary-tint)]"
              style={{ borderColor: "var(--border-soft)" }}
              data-testid={`forfait-cours-${c.id}`}
            >
              <span className="w-[18px] h-[18px] rounded-[5px] border flex items-center justify-center shrink-0"
                style={{ borderColor: choisis.includes(c.id) ? "var(--primary)" : "#CBD5E1", background: choisis.includes(c.id) ? "var(--primary)" : "#fff" }}>
                {choisis.includes(c.id) && <Check size={12} color="#fff" />}
              </span>
              <span className="font-medium">{c.nom}</span>
              <span className="text-[var(--muted)]">{c.jour} {c.debut} · {c.discipline}</span>
              {!(c.statut || "").toLowerCase().startsWith("actif") && <span className="ml-auto text-[10px] text-[var(--muted)]">inactif</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <BoutonSombre
          onClick={() => onValider({ nom, prix: Number(prix) || 0, costume_inclus: costume, cours: choisis })}
          testid="forfait-valider"
          disabled={!nom.trim() || !Number(prix) || choisis.length === 0}
        >
          {forfait ? "Enregistrer le forfait" : "Créer le forfait"}
        </BoutonSombre>
        <button onClick={onFermer} className="text-sm text-[var(--muted)]" data-testid="forfait-annuler">Annuler</button>
        <span className="text-[11px] text-[var(--muted)] ml-auto">
          C'est ce prix qui est facturé, une fois par élève ; un cours retiré du forfait repasse au tarif à la durée.
        </span>
      </div>
    </div>
  );
}

/* Dates de saison : ce que chaque date décide, écrit en clair sous le champ. */
export function Saison({ saison, semestreCourant, onEnregistrer }) {
  const [v, setV] = useState(saison);
  const modifie = JSON.stringify(v) !== JSON.stringify(saison);
  const champs = [
    { cle: "date_limite_spectacle", label: "Bascule spectacle (jj-mm)",
      aide: "Dès cette date, les cours qui montent le spectacle n'acceptent plus d'essai et les nouvelles demandes partent en liste d'attente pour la rentrée." },
    { cle: "echeance_desinscription_s1", label: "Échéance de désinscription — semestre 1",
      aide: "Règlement art. 1 : une désinscription annoncée après cette date laisse le semestre 2 dû." },
    { cle: "echeance_desinscription_s2", label: "Échéance de désinscription — semestre 2",
      aide: "Même règle pour le semestre 1 de la saison suivante." },
    { cle: "audition_date", label: "Date de l'audition (aaaa-mm-jj)",
      aide: "Affichée sur le site (page Compétition, formulaire d'audition, dates importantes). Une fois la date passée, le site n'affiche plus de date et le formulaire d'audition se ferme." },
  ];
  return (
    <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }} data-testid="saison">
      <div className="rounded-xl px-4 py-3 mb-4 text-xs" style={{ background: "#EFF6FF", border: "1px solid #DBEAFE", color: "#1D4ED8" }}>
        <strong>Saison en cours : {String(v.libelle || "").replace("-", "/")}</strong> — elle avance
        toute seule d'une année à l'autre (calendrier officiel vaudois, chargé jusqu'en 2031) : rien
        à saisir, et les saisons précédentes restent consultables dans Facturation et Attestations.
        <br />
        <strong>Le semestre en cours n'est pas à saisir non plus : il est déduit de la date du jour</strong> — semestre 1
        d'août à janvier, semestre 2 de février à juillet. Aujourd'hui : <strong>semestre {semestreCourant}</strong>.
        C'est lui qui décide du semestre d'une inscription ajoutée à la main, de la facturation générée par défaut
        et du décompte des parrainages.
      </div>
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
        {champs.map((c) => (
          <label key={c.cle} className="block">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">{c.label}</span>
            <input
              className="input-field mt-1"
              value={v[c.cle] || ""}
              onChange={(e) => setV({ ...v, [c.cle]: e.target.value })}
              data-testid={`saison-${c.cle}`}
            />
            <span className="text-[11px] text-[var(--muted)] leading-snug block mt-1">{c.aide}</span>
          </label>
        ))}
      </div>
      <div className="mt-4">
        <BoutonSombre onClick={() => onEnregistrer(v)} testid="saison-enregistrer" disabled={!modifie}>Enregistrer</BoutonSombre>
      </div>
    </div>
  );
}
